import { useMemo, useState, useEffect } from 'react';

export default function ScaleDisplay({ slide, responses, participantCount }) {
  const minVal = slide?.config?.min ?? 1;
  const maxVal = slide?.config?.max ?? 5;
  const minLabel = slide?.config?.minLabel || String(minVal);
  const maxLabel = slide?.config?.maxLabel || String(maxVal);
  const steps = maxVal - minVal + 1;

  const { average, distribution, totalResponses } = useMemo(() => {
    const dist = {};
    for (let i = minVal; i <= maxVal; i++) {
      dist[i] = 0;
    }

    let sum = 0;
    let count = 0;

    for (const r of responses) {
      const val = Number(r.value);
      if (!isNaN(val) && val >= minVal && val <= maxVal) {
        sum += val;
        count++;
        dist[Math.round(val)] = (dist[Math.round(val)] || 0) + 1;
      }
    }

    const avg = count > 0 ? sum / count : 0;
    return { average: avg, distribution: dist, totalResponses: count };
  }, [responses, minVal, maxVal]);

  const [displayAvg, setDisplayAvg] = useState(0);

  useEffect(() => {
    if (totalResponses === 0) {
      setDisplayAvg(0);
      return;
    }

    const duration = 800;
    const startTime = performance.now();
    const startVal = displayAvg;
    const endVal = average;

    function animate(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayAvg(startVal + (endVal - startVal) * eased);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }, [average, totalResponses]);

  const maxDistCount = Math.max(...Object.values(distribution), 1);

  if (responses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 w-full">
        <div className="flex items-center gap-4 text-blue-300 text-lg">
          <span>{minLabel}</span>
          <div className="flex gap-1">
            {Array.from({ length: steps }, (_, i) => (
              <div
                key={i}
                className="w-12 h-12 rounded-lg bg-gray-700 border border-gray-600 flex items-center justify-center text-gray-500"
              >
                {minVal + i}
              </div>
            ))}
          </div>
          <span>{maxLabel}</span>
        </div>
        <p className="text-blue-300 text-2xl animate-pulse mt-4">Inga svar enno...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full gap-8">
      <div className="text-center">
        <div className="text-8xl md:text-9xl font-bold text-white tabular-nums">
          {displayAvg.toFixed(1)}
        </div>
        <div className="flex items-center justify-center gap-3 text-blue-300 text-lg mt-2">
          <span>{minLabel}</span>
          <span className="text-blue-500">---</span>
          <span>{maxLabel}</span>
        </div>
      </div>

      <div className="w-full max-w-2xl">
        <div className="flex items-end justify-center gap-2" style={{ height: '140px' }}>
          {Array.from({ length: steps }, (_, i) => {
            const level = minVal + i;
            const count = distribution[level] || 0;
            const heightPct = maxDistCount > 0 ? (count / maxDistCount) * 100 : 0;
            const intensity = steps > 1 ? i / (steps - 1) : 0.5;
            const r = Math.round(147 + (29 - 147) * intensity);
            const g = Math.round(197 + (78 - 197) * intensity);
            const b = Math.round(253 + (216 - 253) * intensity);
            const color = `rgb(${r}, ${g}, ${b})`;

            return (
              <div key={level} className="flex flex-col items-center gap-1 flex-1">
                {count > 0 && (
                  <span className="text-blue-200 text-sm font-medium">{count}</span>
                )}
                <div
                  className="w-full rounded-t-lg transition-all duration-700 ease-out"
                  style={{
                    height: `${Math.max(heightPct, count > 0 ? 8 : 2)}%`,
                    backgroundColor: count > 0 ? color : '#374151',
                    minHeight: '4px',
                  }}
                />
                <span className="text-blue-300 text-sm font-medium mt-1">{level}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-blue-300 text-lg">
        {totalResponses} svar
      </p>
    </div>
  );
}
